/* Copyright (c) 2026 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { useEffect, useRef, useState } from "react";
import {
  Group,
  Resource,
  useGetResourceGroupLazyQuery,
} from "~/generated/graphql";
import { isNil } from "~/tools";

export type ResolvedGroup = {
  groupId: string;
  resources: Resource[];
};

export function useGetAllGroupResources(resourceGroupIds: string[]) {
  const [resolvedGroups, setResolvedGroups] = useState<ResolvedGroup[]>([]);
  const [loading, setLoading] = useState(false);
  const requestIdRef = useRef(0);

  const [fetchGroup] = useGetResourceGroupLazyQuery();

  useEffect(() => {
    let cancelled = false;
    const requestId = ++requestIdRef.current;

    if (!resourceGroupIds?.length) {
      setResolvedGroups([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    Promise.all(
      resourceGroupIds.map((groupId) =>
        fetchGroup({ variables: { resourceGroupId: groupId } })
          .then((res) => ({
            groupId,
            resources: extractResourcesFromGroup(res.data?.group),
          }))
          .catch(() => ({ groupId, resources: [] }))
      )
    )
      .then((result) => {
        if (cancelled || requestId !== requestIdRef.current) {
          return result;
        }

        setResolvedGroups(result);
        return result;
      })
      .finally(() => {
        if (cancelled || requestId !== requestIdRef.current) {
          return;
        }

        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [resourceGroupIds.join(","), fetchGroup]);

  return { resolvedGroups, loading };
}

function extractResourcesFromGroup(
  group: Group | null | undefined
): Resource[] {
  if (isNil(group) || isNil(group?.resourceSelectors)) {
    return [];
  }

  const staticResources =
    group?.resourceSelectors.flatMap((item) => item?.resource ?? []) ?? [];

  const dynamicResources =
    group?.resourceSelectors.flatMap((item) => item?.dynamicResources ?? []) ??
    [];

  return [...staticResources, ...dynamicResources];
}
